import api from '../api/client';

/**
 * Fetches the public social login configuration for an app.
 * Returns only enabled providers with their public client_id.
 * No auth token required — called before login.
 *
 * @param {string} appId
 * @returns {Promise<Array<{ provider: string, is_enabled: number, client_id: string }>>}
 */
const getSocialConfig = async (appId) => {
    const res = await api.get('/api/auth/social-config', { params: { app_id: appId } });
    return res?.data?.data || [];
};

/**
 * Authenticates via a social provider ID token.
 * Issues authZ + refresh tokens on success.
 *
 * @param {object} payload
 * @param {string} payload.provider - 'google' | 'apple'
 * @param {string} payload.id_token - Provider-issued ID token
 * @param {string} payload.app_id   - Application ID
 * @param {object} [payload.apple_user] - Optional { firstName, lastName } for Apple first-login
 * @returns {Promise<{ token: string, refreshToken: string, username: string }>}
 */
const socialLogin = async (payload) => {
    const res = await api.post('/api/auth/social-login', payload);
    return res?.data?.data;
};

/**
 * Fetches all OAuth provider configs for an app (admin-only).
 * private_key is masked — returns has_private_key boolean instead.
 * Also returns centralConfigs (platform-wide defaults) for fallback context.
 *
 * @param {string} appId
 * @returns {Promise<{ data: Array, centralConfigs: Array }>}
 */
const getOAuthConfigs = async (appId) => {
    const res = await api.get('/api/application/oauth-config', { params: { app_id: appId } });
    return {
        data: res?.data?.data || [],
        centralConfigs: res?.data?.centralConfigs || [],
    };
};

/**
 * Creates or updates an OAuth provider config for an app.
 *
 * @param {string} appId
 * @param {object} payload
 * @param {string} payload.provider    - 'google' | 'apple'
 * @param {number} payload.is_enabled  - 0 | 1
 * @param {string} [payload.client_id] - Google Client ID or Apple Service ID
 * @param {string} [payload.team_id]   - Apple Team ID
 * @param {string} [payload.key_id]    - Apple Key ID
 * @param {string} [payload.private_key] - Apple p8 private key (omit to keep existing)
 * @returns {Promise<object>}
 */
const upsertOAuthConfig = async (appId, payload) => {
    const res = await api.post('/api/application/oauth-config', payload, {
        params: { app_id: appId },
    });
    return res?.data;
};

/**
 * Hard-deletes an OAuth provider config for an app.
 *
 * @param {string} appId
 * @param {string} provider - 'google' | 'apple'
 * @returns {Promise<object>}
 */
const deleteOAuthConfig = async (appId, provider) => {
    const res = await api.delete(`/api/application/oauth-config/${provider}`, {
        params: { app_id: appId },
    });
    return res?.data;
};

/**
 * Fetches all central (platform-wide default) OAuth configs (portal admin only).
 * @returns {Promise<Array>}
 */
const getCentralOAuthConfigs = async () => {
    const res = await api.get('/api/application/central-oauth-config');
    return res?.data?.data || [];
};

/**
 * Creates or updates a central OAuth provider config (portal admin only).
 * @param {object} payload
 * @returns {Promise<object>}
 */
const upsertCentralOAuthConfig = async (payload) => {
    const res = await api.post('/api/application/central-oauth-config', payload);
    return res?.data;
};

/**
 * Hard-deletes a central OAuth provider config (portal admin only).
 * @param {string} provider - 'google' | 'apple'
 * @returns {Promise<object>}
 */
const deleteCentralOAuthConfig = async (provider) => {
    const res = await api.delete(`/api/application/central-oauth-config/${provider}`);
    return res?.data;
};

const oauthConfigService = {
    getSocialConfig,
    socialLogin,
    getOAuthConfigs,
    upsertOAuthConfig,
    deleteOAuthConfig,
    getCentralOAuthConfigs,
    upsertCentralOAuthConfig,
    deleteCentralOAuthConfig,
};

export default oauthConfigService;
